import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import type { WorkspaceKind } from '../config/workspaceKinds';

interface Props {
  kind: WorkspaceKind;
  /** Called when the user picks a quick-action so the parent can push
   *  the text into the chat input (does NOT auto-send). */
  onInsertPrompt: (prompt: string) => void;
  className?: string;
}

/**
 * WorkspaceContextChips — contextual quick-action chips that appear
 * above the chat input. They are the CATEGORICAL implementation of the
 * "Smart Workspaces" objective: each project type ships its own
 * curated set of prompts (Landing → Hero/CTA/FAQ/SEO/…, App → UI/State/
 * Data/Flows/…), so the user always sees the actions relevant to what
 * they're actually building.
 *
 * Design principles:
 *   • Same visual identity across workspace kinds (glass surface, accent
 *     ring) — the CONTENT changes, not the look.
 *   • Chips insert the prompt into the input; they never auto-send —
 *     the user always reviews before pressing enter.
 *   • Collapsible so it doesn't crowd the chat when the user is deep
 *     in a session. Remembers state in localStorage per workspace kind.
 */
export const WorkspaceContextChips: React.FC<Props> = ({
  kind, onInsertPrompt, className = '',
}) => {
  const storageKey = `nx.chips.${kind.key}.open`;
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const raw = window.localStorage.getItem(storageKey);
    return raw === null ? true : raw === '1';
  });

  const grouped = useMemo(() => {
    const groups: Record<string, typeof kind.quickPrompts> = {};
    for (const p of kind.quickPrompts) {
      const g = p.group || 'Acciones';
      (groups[g] = groups[g] || []).push(p);
    }
    return Object.entries(groups);
  }, [kind.quickPrompts]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, next ? '1' : '0');
    }
  };

  if (kind.quickPrompts.length === 0) return null;

  return (
    <div
      className={`w-full rounded-2xl px-3 py-2 ${className}`}
      style={{
        background: 'hsl(var(--bg-elev) / 0.4)',
        border: '1px solid hsl(var(--border) / 0.7)',
        backdropFilter: 'blur(12px)',
      }}
      data-testid="workspace-context-chips"
    >
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="workspace-context-chips-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-6 h-6 rounded-lg grid place-items-center shrink-0"
            style={{
              background: `${kind.accent.replace(')', ' / 0.16)')}`,
              border: `1px solid ${kind.accent.replace(')', ' / 0.35)')}`,
            }}
          >
            <Sparkles className="w-3 h-3" style={{ color: kind.accent }} />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold" style={{ color: 'hsl(var(--text))' }}>
              Herramientas de {kind.label}
            </div>
            {open && (
              <div className="text-[10px] opacity-60 truncate">
                {kind.tagline}
              </div>
            )}
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 opacity-60" />
        ) : (
          <ChevronDown className="w-4 h-4 opacity-60" />
        )}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 space-y-2">
              {grouped.map(([groupName, prompts]) => (
                <div key={groupName}>
                  <div
                    className="text-[9px] uppercase tracking-widest opacity-50 mb-1"
                    style={{ color: 'hsl(var(--text-muted))' }}
                  >
                    {groupName}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {prompts.map((p) => (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => onInsertPrompt(p.prompt)}
                        data-testid={`quick-prompt-${p.label.replace(/\s+/g, '-').toLowerCase()}`}
                        className="text-[11px] px-2.5 py-1 rounded-full transition hover:scale-[1.02]"
                        style={{
                          background: 'hsl(var(--bg-elev) / 0.6)',
                          border: '1px solid hsl(var(--border))',
                          color: 'hsl(var(--text))',
                        }}
                        title={p.prompt}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WorkspaceContextChips;
